import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getSessionHistory,
  getSessionConfig,
  getSessionMessages,
} from '../../services/api';
import LanternMark from '../../components/LanternMark';

// ─── Brand tokens — mirrors Chat.jsx ──────────────────────────────────────────

const C = {
  bg: '#FFFDF7',
  text: '#1A1F36',
  textMuted: '#5A6478',
  textSubtle: '#8A93A6',
  accent: '#F59E0B',
  accentDark: '#D97706',
  accentSoft: '#FEF3C7',
  green: '#10B981',
  card: '#FFFFFF',
  border: '#F0E7D5',
  borderInk: '#D9D2BF',
  errorBg: '#FEF2F2',
  errorText: '#B42318',
  errorBorder: '#FECACA',
};

const HEADING_FONT = "'Nunito', sans-serif";
const BODY_FONT = "'Source Sans 3', sans-serif";

const MODE_LABEL = {
  homework:    'Homework',
  explore:     'Explore',
  revision:    'Revision',
  code:        'Code',
  eleven_plus: '11+ Prep',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getSubjectMeta(subjectKey, configSubjects) {
  if (subjectKey === 'general' || !subjectKey) {
    return { displayName: 'Anything', icon: '✨', key: 'general' };
  }
  const match = configSubjects?.find((s) => s.key === subjectKey);
  return {
    displayName: match?.displayName || subjectKey,
    icon:        match?.icon        || '📖',
    key:         subjectKey,
  };
}

function formatDate(ts) {
  if (!ts) return '';
  const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
  const startOfThen  = new Date(ts); startOfThen.setHours(0, 0, 0, 0);
  const dayDiff = Math.round((startOfToday - startOfThen) / 86400000);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  if (dayDiff < 7)   return `${dayDiff} days ago`;
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

// Group an array of sessions by subject. Keeps subject order stable by first
// appearance so the most-recently-active subject sits at the top of the list.
function groupBySubject(sessions, configSubjects) {
  const groups = [];
  const seen = new Map();
  for (const s of sessions) {
    const meta = getSubjectMeta(s.subject, configSubjects);
    const key = meta.key;
    if (!seen.has(key)) {
      seen.set(key, groups.length);
      groups.push({ key, displayName: meta.displayName, icon: meta.icon, sessions: [] });
    }
    groups[seen.get(key)].sessions.push(s);
  }
  return groups;
}

// ─── Read-only message bubble — mirrors Chat.jsx styling without ReportButton ─

const MONO_FONT = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
const BLOCK_BOTTOM = '0 0 10px';

const MD_COMPONENTS = {
  p: ({ node, ...props }) => <p style={{ margin: BLOCK_BOTTOM }} {...props} />,
  ul: ({ node, ordered, ...props }) => <ul style={{ margin: BLOCK_BOTTOM, paddingLeft: 22 }} {...props} />,
  ol: ({ node, ordered, ...props }) => <ol style={{ margin: BLOCK_BOTTOM, paddingLeft: 22 }} {...props} />,
  li: ({ node, ordered, checked, ...props }) => <li style={{ margin: '2px 0' }} {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      style={{ margin: BLOCK_BOTTOM, padding: '4px 12px', borderLeft: `3px solid ${C.accent}`, opacity: 0.92 }}
      {...props}
    />
  ),
  code: ({ node, className, children, ...props }) => {
    const isFencedBlock = /language-/.test(className || '')
      || (typeof children === 'string' && children.includes('\n'))
      || (Array.isArray(children) && children.some(c => typeof c === 'string' && c.includes('\n')));
    if (isFencedBlock) {
      return <code className={className} {...props}>{children}</code>;
    }
    return (
      <code
        style={{
          background: 'rgba(255,255,255,0.12)',
          padding: '1px 6px',
          borderRadius: 4,
          fontFamily: MONO_FONT,
          fontSize: '0.92em',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({ node, children, ...props }) => {
    const child = React.Children.toArray(children)[0];
    const codeClass = child && child.props ? (child.props.className || '') : '';
    const langMatch = /language-(\w+)/.exec(codeClass);
    const language = langMatch ? langMatch[1] : null;
    return (
      <div style={{ position: 'relative', margin: BLOCK_BOTTOM }}>
        {language && (
          <span style={{
            position: 'absolute', top: 6, right: 10,
            fontSize: 10.5, color: 'rgba(255,255,255,0.55)',
            fontFamily: MONO_FONT, letterSpacing: '0.6px',
            textTransform: 'lowercase', pointerEvents: 'none', userSelect: 'none',
          }}>
            {language}
          </span>
        )}
        <pre style={{
          margin: 0,
          background: 'rgba(0,0,0,0.32)',
          padding: language ? '12px 16px 12px 14px' : '12px 14px',
          borderRadius: 10,
          fontFamily: MONO_FONT,
          fontSize: '0.92em',
          overflowX: 'auto',
          whiteSpace: 'pre',
          lineHeight: 1.5,
        }} {...props}>{children}</pre>
      </div>
    );
  },
};

function LanternMarkdown({ children }) {
  return (
    <>
      <style>{`
        .lantern-md > *:last-child { margin-bottom: 0 !important; }
      `}</style>
      <div className="lantern-md">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>
          {children}
        </ReactMarkdown>
      </div>
    </>
  );
}

function MessageBubble({ message }) {
  const isChild = message.role === 'child';

  if (isChild) {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
        <div style={{
          maxWidth: '78%',
          background: `linear-gradient(135deg, ${C.accent} 0%, ${C.accentDark} 100%)`,
          color: '#fff',
          borderRadius: '22px 22px 6px 22px',
          padding: '12px 16px',
          fontSize: 16,
          lineHeight: 1.5,
          fontFamily: BODY_FONT,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          boxShadow: `0 2px 8px ${C.accent}40, 0 1px 2px ${C.accentDark}30`,
          fontWeight: 500,
        }}>
          {message.content}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: 14, gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, maxWidth: '88%' }}>
        <div style={{ flexShrink: 0, marginBottom: 2 }}>
          <LanternMark size={28} glow={false} />
        </div>
        <div style={{
          background: C.text,
          color: '#FFFEFB',
          borderRadius: '22px 22px 22px 6px',
          padding: '12px 16px',
          fontSize: 16,
          lineHeight: 1.65,
          letterSpacing: '0.5px',
          fontFamily: BODY_FONT,
          wordBreak: 'break-word',
          boxShadow: '0 2px 8px rgba(26, 31, 54, 0.18), 0 1px 2px rgba(26, 31, 54, 0.08)',
        }}>
          <LanternMarkdown>{message.content}</LanternMarkdown>
        </div>
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export default function SessionHistory() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [config, setConfig]                 = useState(null);
  const [sessions, setSessions]             = useState([]);
  const [loadError, setLoadError]           = useState(null);
  // Distinct from loadError on purpose: loadError means "the list is
  // unusable" and hides it. This one sits ABOVE a list that is fine.
  const [notice, setNotice]                 = useState(null);
  const [listLoading, setListLoading]       = useState(true);
  const [selected, setSelected]             = useState(null); // selected session row
  const [messages, setMessages]             = useState([]);
  const [detailLoading, setDetailLoading]   = useState(false);
  const [detailError, setDetailError]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getSessionConfig().catch(() => null),
      getSessionHistory().catch(() => null),
    ])
      .then(([cfgRes, sessRes]) => {
        if (cancelled) return;
        if (!cfgRes || !sessRes) {
          setLoadError('Could not load your past sessions. Please try again.');
          setListLoading(false);
          return;
        }
        setConfig(cfgRes.data);
        // Already filtered to finished sessions and ordered newest-first by
        // the server. This used to call getSessions() and filter to sealed
        // ones here — filtering after the server's limit, so open sessions
        // ate history slots and anything older than the tenth most recent
        // session could not be reached at all.
        const sealed = sessRes.data?.sessions || [];
        setSessions(sealed);
        setListLoading(false);

        // Auto-open a specific session when arriving with ?sessionId=...
        // This is how the SubjectSelector "Continue your last session"
        // section drops the child straight into the detail view, where
        // the "Continue chatting" CTA is waiting.
        const wantedId = searchParams.get('sessionId');
        if (wantedId) {
          const match = sealed.find((s) => String(s.id) === String(wantedId));
          if (match) {
            openSession(match);
          } else {
            // Arriving with a sessionId that isn't in the list used to do
            // nothing at all — the child tapped "Continue" and landed on a
            // list that just sat there. Say something instead of failing
            // silently; the list itself is still perfectly usable.
            setNotice("We couldn't find that chat, but your other chats are below.");
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          navigate('/login');
          return;
        }
        setLoadError('Could not load your past sessions. Please try again.');
        setListLoading(false);
      });
    return () => { cancelled = true; };
    // searchParams is read inside for the ?sessionId= auto-open. The URL
    // doesn't change during a session — including it in deps is the
    // react-router idiom; the cancelled flag protects against an in-flight
    // re-fire if it ever did.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, searchParams]);

  function openSession(session) {
    setSelected(session);
    setMessages([]);
    setDetailError(null);
    setDetailLoading(true);
    getSessionMessages(session.id)
      .then((res) => {
        const stored = (res.data?.messages || []).map((m) => ({
          id:      m.id,
          role:    m.role,
          content: m.content,
        }));
        setMessages(stored);
        setDetailLoading(false);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          navigate('/login');
          return;
        }
        setDetailError("Couldn't load this session. Try again?");
        setDetailLoading(false);
      });
  }

  function closeDetail() {
    setSelected(null);
    setMessages([]);
    setDetailError(null);
  }

  function studyAgain() {
    if (!selected) return;
    const params = new URLSearchParams();
    if (selected.subject) params.set('subject', selected.subject);
    if (selected.mode)    params.set('mode',    selected.mode);
    const qs = params.toString();
    navigate(`/child/subjects${qs ? `?${qs}` : ''}`);
  }

  function continueChatting() {
    if (!selected) return;
    const params = new URLSearchParams();
    if (selected.subject) params.set('subject', selected.subject);
    if (selected.mode)    params.set('mode',    selected.mode);
    params.set('continuePrevious', 'true');
    navigate(`/child/subjects?${params.toString()}`);
  }

  // "Continue chatting" is offered when the selected session was sealed
  // within the last 2 hours. Anything older falls back to "Study this
  // topic again" only — the prior-session continuity helper on the
  // server bounds carry-forward at 24h, but UX-wise two hours is the
  // window where the child likely still remembers the conversation and
  // is genuinely picking up rather than starting fresh.
  const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
  const sealedRecently = !!(
    selected && selected.ended_at && (Date.now() - selected.ended_at) < TWO_HOURS_MS
  );

  const groups = config ? groupBySubject(sessions, config.subjects) : [];

  return (
    <>
      <style>{`
        @keyframes hist-pop {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes hist-spin { to { transform: rotate(360deg); } }
        .hist-card { animation: hist-pop 280ms cubic-bezier(0.2,0.8,0.2,1) both; }
        .hist-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 16px;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          box-shadow: 0 2px 0 ${C.border};
          width: 100%;
          text-align: left;
          font-family: ${BODY_FONT};
          color: ${C.text};
          cursor: pointer;
          transition: border-color 160ms ease, background 160ms ease, transform 90ms ease;
          -webkit-tap-highlight-color: transparent;
        }
        .hist-row:hover { border-color: ${C.accent}99; background: #FFFBF0; }
        .hist-row:active { transform: translateY(1px); box-shadow: 0 1px 0 ${C.border}; }
        .hist-row:focus-visible {
          outline: none;
          box-shadow: 0 2px 0 ${C.border}, 0 0 0 4px ${C.accent}55;
        }
        .hist-back {
          padding: 9px 14px;
          border-radius: 12px;
          border: 1.5px solid ${C.border};
          background: ${C.card};
          color: ${C.text};
          font-family: ${HEADING_FONT};
          font-size: 13px;
          font-weight: 800;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
          -webkit-tap-highlight-color: transparent;
        }
        .hist-back:hover { background: ${C.accentSoft}; border-color: ${C.accent}; color: ${C.accentDark}; }
        .hist-study-again {
          width: 100%;
          min-height: 52px;
          padding: 14px 22px;
          background: ${C.accent};
          color: #fff;
          border: none;
          border-radius: 16px;
          font-family: ${HEADING_FONT};
          font-size: 17px;
          font-weight: 800;
          letter-spacing: 0.3px;
          cursor: pointer;
          box-shadow: 0 4px 0 ${C.accentDark}, 0 8px 20px ${C.accent}40;
          transition: transform 90ms ease, background 160ms ease, box-shadow 160ms ease;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .hist-study-again:hover { background: ${C.accentDark}; }
        .hist-study-again:active {
          transform: translateY(3px);
          box-shadow: 0 1px 0 ${C.accentDark}, 0 4px 10px ${C.accent}40;
        }
        .hist-spinner {
          width: 32px; height: 32px; border-radius: 50%;
          border: 3px solid ${C.border};
          border-top-color: ${C.accent};
          animation: hist-spin 800ms linear infinite;
        }
      `}</style>

      <div style={{
        minHeight: '100vh',
        background: `${C.bg} radial-gradient(ellipse 600px 400px at 50% -10%, rgba(245,158,11,0.11) 0%, rgba(245,158,11,0) 60%)`,
        fontFamily: BODY_FONT,
        color: C.text,
        boxSizing: 'border-box',
      }}>
        {/* ── Detail view ────────────────────────────────────────── */}
        {selected ? (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Header — title only; back button lives in the footer per spec. */}
            <header style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              background: C.card,
              borderBottom: `1px solid ${C.border}`,
              // env(safe-area-inset-top) keeps the title row clear of the iOS
              // status bar / camera notch when launched as a standalone PWA.
              padding: 'calc(14px + env(safe-area-inset-top)) 16px 12px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <LanternMark size={28} glow={false} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  margin: 0,
                  fontFamily: HEADING_FONT,
                  fontWeight: 800,
                  fontSize: 16,
                  color: C.text,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {getSubjectMeta(selected.subject, config?.subjects).displayName}
                </p>
                <p style={{
                  margin: '2px 0 0',
                  fontSize: 12.5,
                  color: C.textMuted,
                  fontWeight: 600,
                }}>
                  {MODE_LABEL[selected.mode] || selected.mode} · {formatDate(selected.started_at)}
                </p>
              </div>
            </header>

            {/* Messages */}
            <main style={{ flex: 1, padding: '20px 16px 14px' }}>
              <div style={{ maxWidth: 720, margin: '0 auto' }}>
                {detailLoading && (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 12,
                    padding: '60px 20px',
                    color: C.textMuted,
                  }}>
                    <div className="hist-spinner" aria-hidden="true" />
                    <p style={{ margin: 0, fontFamily: HEADING_FONT, fontWeight: 700 }}>Loading…</p>
                  </div>
                )}

                {detailError && (
                  <div role="alert" style={{
                    padding: '12px 14px',
                    background: C.errorBg,
                    color: C.errorText,
                    borderRadius: 16,
                    border: `1.5px solid ${C.errorBorder}`,
                    fontWeight: 600,
                    fontSize: 14,
                    textAlign: 'center',
                  }}>
                    {detailError}
                  </div>
                )}

                {!detailLoading && !detailError && messages.map((m) => (
                  <MessageBubble key={m.id} message={m} />
                ))}

                {!detailLoading && !detailError && messages.length === 0 && (
                  <p style={{ textAlign: 'center', color: C.textMuted, marginTop: 40 }}>
                    No messages in this session.
                  </p>
                )}
              </div>
            </main>

            {/* Back + Continue chatting (recent) + Study again CTAs at the bottom.
                When the session was sealed within 2 hours we promote a full-
                width "Continue chatting" CTA above the existing row so the
                child can pick up where they left off without a full restart.
                The server's prior-session continuity helper does the rest —
                it injects the last few turns of the sealed session into the
                new chat. */}
            <footer style={{
              flexShrink: 0,
              background: C.card,
              borderTop: `1px solid ${C.border}`,
              padding: '14px 16px 18px',
              boxShadow: '0 -2px 12px rgba(26, 31, 54, 0.04)',
            }}>
              <div style={{
                maxWidth: 720,
                margin: '0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}>
                {sealedRecently && (
                  <button
                    type="button"
                    className="hist-study-again"
                    onClick={continueChatting}
                    aria-label="Continue chatting in this session"
                    style={{ width: '100%' }}
                  >
                    Continue chatting 💬
                  </button>
                )}
                <div style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                }}>
                  <button
                    type="button"
                    className="hist-back"
                    onClick={closeDetail}
                    aria-label="Back to past sessions"
                    style={{ flexShrink: 0 }}
                  >
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M9 2 L4 7 L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Back
                  </button>
                  <button
                    type="button"
                    className="hist-study-again"
                    onClick={studyAgain}
                    style={{ flex: 1 }}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                      <path d="M5 2 L14 9 L5 16 Z" fill="#fff" />
                    </svg>
                    Study this topic again
                  </button>
                </div>
              </div>
            </footer>
          </div>
        ) : (
          // ── List view ──────────────────────────────────────────
          <div style={{ padding: 'calc(28px + env(safe-area-inset-top)) 18px 40px' }}>
            <div className="hist-card" style={{ maxWidth: 460, margin: '0 auto' }}>
              {/* Header */}
              <header style={{ marginBottom: 22 }}>
                <button
                  type="button"
                  className="hist-back"
                  onClick={() => navigate('/child/subjects')}
                  aria-label="Back to subjects"
                  style={{ marginBottom: 14 }}
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M9 2 L4 7 L9 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Back
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                  <LanternMark size={36} glow={false} />
                  <h1 style={{
                    margin: 0,
                    fontFamily: HEADING_FONT,
                    fontSize: 24,
                    fontWeight: 800,
                    letterSpacing: '-0.5px',
                    color: C.text,
                  }}>
                    My past sessions
                  </h1>
                </div>
                <p style={{ margin: 0, color: C.textMuted, fontSize: 14, lineHeight: 1.45 }}>
                  Tap a session to read it again.
                </p>
              </header>

              {listLoading && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 14,
                  padding: '60px 20px',
                }}>
                  <div className="hist-spinner" aria-hidden="true" />
                  <p style={{ margin: 0, color: C.textMuted, fontFamily: HEADING_FONT, fontWeight: 700 }}>
                    Loading…
                  </p>
                </div>
              )}

              {loadError && !listLoading && (
                <div role="alert" style={{
                  padding: '12px 14px',
                  background: C.errorBg,
                  color: C.errorText,
                  borderRadius: 16,
                  border: `1.5px solid ${C.errorBorder}`,
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                }}>
                  {loadError}
                </div>
              )}

              {/* Sits above a list that is working, so it is deliberately
                  softer than the error styling and does NOT gate the list
                  below it. */}
              {notice && !listLoading && !loadError && (
                <div role="status" style={{
                  padding: '10px 14px',
                  marginBottom: 12,
                  background: C.bg,
                  color: C.textMuted,
                  borderRadius: 16,
                  border: `1.5px solid ${C.errorBorder}`,
                  fontSize: 14,
                  fontWeight: 600,
                  textAlign: 'center',
                }}>
                  {notice}
                </div>
              )}

              {!listLoading && !loadError && sessions.length === 0 && (
                <div style={{
                  padding: '40px 20px',
                  textAlign: 'center',
                  color: C.textMuted,
                }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }} aria-hidden="true">📚</div>
                  <p style={{
                    margin: 0,
                    fontFamily: HEADING_FONT,
                    fontWeight: 800,
                    color: C.text,
                    fontSize: 17,
                  }}>
                    No past sessions yet
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: 14, lineHeight: 1.45 }}>
                    Sessions show up here once you end them.
                  </p>
                </div>
              )}

              {!listLoading && !loadError && groups.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {groups.map((group) => (
                    <section key={group.key}>
                      <p style={{
                        fontFamily: HEADING_FONT,
                        fontSize: 13,
                        fontWeight: 800,
                        color: C.textMuted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        margin: '0 0 10px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                      }}>
                        <span aria-hidden="true" style={{ fontSize: 16 }}>{group.icon}</span>
                        {group.displayName}
                      </p>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {group.sessions.map((s) => {
                          const count = s.message_count || 0;
                          const modeLabel = MODE_LABEL[s.mode] || s.mode;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              className="hist-row"
                              onClick={() => openSession(s)}
                            >
                              <span aria-hidden="true" style={{
                                width: 40, height: 40, borderRadius: 12,
                                background: C.accentSoft,
                                border: `1.5px solid ${C.border}`,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20, flexShrink: 0,
                              }}>
                                {group.icon}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <p style={{
                                  margin: 0,
                                  fontFamily: HEADING_FONT,
                                  fontSize: 15,
                                  fontWeight: 800,
                                  color: C.text,
                                }}>
                                  {modeLabel} · {formatDate(s.started_at)}
                                </p>
                                <p style={{
                                  margin: '2px 0 0',
                                  fontSize: 12.5,
                                  color: C.textMuted,
                                  fontWeight: 600,
                                }}>
                                  {count} {count === 1 ? 'message' : 'messages'}
                                </p>
                              </div>
                              <span aria-hidden="true" style={{
                                color: C.textSubtle,
                                fontSize: 18,
                                flexShrink: 0,
                              }}>
                                ›
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </section>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}
